import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal, ModalOverlay, ModalContent, Flex, Box, Text, Avatar, IconButton,
  Tabs, TabList, TabPanels, Tab, TabPanel, Textarea, Spinner, Center, HStack,
  VStack, Input, Button, Image, Link,
  Menu, MenuButton, MenuList, MenuItem, useToast, Tooltip,
} from "@chakra-ui/react";
import {
  X, Phone, Send, Paperclip, Mic, Image as ImageIcon, FileText, Search,
  Check, CheckCheck, AlertCircle, Trash2,
} from "lucide-react";
import {
  getCrmContato, getCrmMensagens, enviarCrmMensagem, marcarCrmLido,
  getCrmFichaPorContato, getCrmMensagensPoli,
} from "../../services/api";
import { useSoftphone } from "../../contexts/softphone";
import { formatPhone, paraDiscagem, horaMsg, formatDateTime } from "./crmUtils";
import FichaCliente from "./FichaCliente";

const ACCENT = "#F68633";

const StatusIcon = ({ status }) => {
  if (status === "erro") return <AlertCircle size={13} color="#E53E3E" />;
  if (status === "lida") return <CheckCheck size={13} color="#3182CE" />;
  if (status === "entregue") return <CheckCheck size={13} color="#9CA3AF" />;
  return <Check size={13} color="#9CA3AF" />;
};

function Bubble({ m, comData }) {
  const eu = m.direcao === "enviada";
  return (
    <Flex justify={eu ? "flex-end" : "flex-start"} px={1}>
      <Box
        maxW="78%" bg={eu ? "#dcf8c6" : "white"} borderWidth="1px" borderColor="gray.100"
        borderRadius="lg" px={3} py={2} boxShadow="xs"
      >
        {m.tipo === "image" && m.media_url && (
          <Image src={m.media_url} borderRadius="md" mb={m.conteudo ? 1 : 0} maxH="240px" objectFit="cover" />
        )}
        {m.tipo === "audio" && m.media_url && (
          <audio controls src={m.media_url} style={{ maxWidth: "240px" }} />
        )}
        {m.tipo === "video" && m.media_url && (
          <video controls src={m.media_url} style={{ maxWidth: "260px", borderRadius: "8px" }} />
        )}
        {m.tipo === "document" && m.media_url && (
          <Link href={m.media_url} isExternal>
            <HStack spacing={2} bg="gray.50" p={2} borderRadius="md">
              <FileText size={18} />
              <Text fontSize="xs" noOfLines={1}>{m.media_nome || "Documento"}</Text>
            </HStack>
          </Link>
        )}
        {m.conteudo && <Text fontSize="sm" whiteSpace="pre-wrap">{m.conteudo}</Text>}
        {!m.conteudo && !m.media_url && ["location", "contact", "sticker"].includes(m.tipo) && (
          <Text fontSize="sm" fontStyle="italic" color="gray.500">[{m.tipo}]</Text>
        )}
        <HStack spacing={1} justify="flex-end" mt={0.5}>
          <Text fontSize="9px" color="gray.400">{comData ? formatDateTime(m.enviada_em) : horaMsg(m.enviada_em)}</Text>
          {eu && <StatusIcon status={m.status} />}
        </HStack>
      </Box>
    </Flex>
  );
}

export default function ChatModal({ contatoId, isOpen, onClose }) {
  const [info, setInfo] = useState(null);
  const [tabIdx, setTabIdx] = useState(0);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [limite, setLimite] = useState(50);
  const [busca, setBusca] = useState("");
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [pending, setPending] = useState(null); // { file, tipo, previewUrl }
  const [caption, setCaption] = useState("");
  const [gravando, setGravando] = useState(false);
  const [recSeg, setRecSeg] = useState(0);

  const sp = useSoftphone();
  const toast = useToast();
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const fileTipoRef = useRef("document");
  const recRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const qtdRef = useRef(0);

  const carregarInfo = useCallback(async () => {
    if (!contatoId) return;
    const r = await getCrmContato(contatoId);
    if (r?.data) setInfo(r);
  }, [contatoId]);

  const carregarMsgs = useCallback(async (silencioso = false) => {
    if (!contatoId) return;
    if (!silencioso) setLoading(true);
    const r = await getCrmMensagens(contatoId, { per_page: limite, search: busca || undefined });
    if (r?.data) {
      const lista = [...r.data].reverse(); // backend devolve desc; exibimos asc
      setMsgs(lista);
    }
    setLoading(false);
  }, [contatoId, limite, busca]);

  // Abertura: carrega tudo + marca lido
  useEffect(() => {
    if (!isOpen || !contatoId) return;
    setInfo(null); setMsgs([]); setTexto(""); setBusca(""); setBuscaAberta(false); setLimite(50);
    carregarInfo();
    carregarMsgs();
    marcarCrmLido(contatoId);
  }, [isOpen, contatoId]); // eslint-disable-line

  useEffect(() => { if (isOpen) carregarMsgs(); }, [limite, busca]); // eslint-disable-line

  // Polling das mensagens enquanto aberto
  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => carregarMsgs(true), 4000);
    return () => clearInterval(t);
  }, [isOpen, carregarMsgs]);

  // Auto-scroll quando chegam mensagens novas + marca lido se a nova é recebida
  useEffect(() => {
    if (msgs.length !== qtdRef.current) {
      const cresceu = msgs.length > qtdRef.current;
      qtdRef.current = msgs.length;
      const el = scrollRef.current;
      if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 50);
      if (cresceu && msgs.length && msgs[msgs.length - 1].direcao === "recebida" && contatoId) {
        marcarCrmLido(contatoId);
      }
    }
  }, [msgs]); // eslint-disable-line react-hooks/exhaustive-deps

  const enviarTexto = async () => {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    const r = await enviarCrmMensagem(contatoId, { tipo: "text", conteudo: t });
    setEnviando(false);
    if (r?.data) { setTexto(""); carregarMsgs(true); }
    else toast({ status: "error", title: "Falha ao enviar", description: r?.error });
  };

  const escolherArquivo = (tipo) => {
    fileTipoRef.current = tipo;
    if (fileRef.current) {
      fileRef.current.accept = tipo === "image" ? "image/*" : "*/*";
      fileRef.current.value = "";
      fileRef.current.click();
    }
  };

  const onFileSelecionado = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tipo = fileTipoRef.current;
    setPending({ file, tipo, previewUrl: tipo === "image" ? URL.createObjectURL(file) : null });
    setCaption("");
  };

  const enviarMidia = async () => {
    if (!pending || enviando) return;
    setEnviando(true);
    const fd = new FormData();
    fd.append("tipo", pending.tipo);
    fd.append("media", pending.file);
    if (caption.trim()) fd.append("conteudo", caption.trim());
    const r = await enviarCrmMensagem(contatoId, fd);
    setEnviando(false);
    if (r?.data) { setPending(null); setCaption(""); carregarMsgs(true); }
    else toast({ status: "error", title: "Falha ao enviar mídia", description: r?.error });
  };

  // ── Gravação de áudio ──
  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) recChunksRef.current.push(ev.data); };
      mr.onstop = () => stream.getTracks().forEach((tr) => tr.stop());
      mr.start();
      recRef.current = mr;
      setGravando(true);
      setRecSeg(0);
      recTimerRef.current = setInterval(() => setRecSeg((s) => s + 1), 1000);
    } catch (e) {
      toast({ status: "error", title: "Microfone indisponível", description: "Permita o acesso ao microfone." });
    }
  };

  const pararEnviarAudio = async () => {
    const mr = recRef.current;
    if (!mr) return;
    clearInterval(recTimerRef.current);
    setGravando(false);
    mr.onstop = async () => {
      mr.stream?.getTracks?.().forEach((tr) => tr.stop());
      const blob = new Blob(recChunksRef.current, { type: "audio/webm" });
      if (blob.size > 0) {
        setEnviando(true);
        const fd = new FormData();
        fd.append("tipo", "audio");
        fd.append("media", blob, "audio.webm");
        const r = await enviarCrmMensagem(contatoId, fd);
        setEnviando(false);
        if (r?.data) carregarMsgs(true);
        else toast({ status: "error", title: "Falha ao enviar áudio", description: r?.error });
      }
      recRef.current = null;
    };
    mr.stop();
  };

  const cancelarGravacao = () => {
    const mr = recRef.current;
    clearInterval(recTimerRef.current);
    setGravando(false);
    if (mr) { mr.onstop = () => mr.stream?.getTracks?.().forEach((tr) => tr.stop()); mr.stop(); recRef.current = null; }
    recChunksRef.current = [];
  };

  const ligar = () => {
    if (!contato) return;
    if (!sp.registered) {
      toast({ status: "warning", title: "Ramal desconectado", description: "Conecte o softphone no Discador antes de ligar." });
      return;
    }
    sp.dial(paraDiscagem(contato.telefone));
    toast({ status: "info", title: "Discando...", description: formatPhone(contato.telefone) });
  };

  const contato = info?.data;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={tabIdx === 2 ? { base: "full", md: "5xl" } : { base: "full", md: "xl", lg: "3xl" }} isCentered>
      <ModalOverlay />
      <ModalContent
        h={{ base: "100dvh", md: "86vh" }} maxH={{ base: "100dvh", md: "86vh" }} my={{ base: 0, md: "auto" }}
        display="flex" flexDirection="column" overflow="hidden" borderRadius={{ base: 0, md: "xl" }}
      >
        {/* Header */}
        <Flex align="center" gap={3} px={4} py={3} borderBottom="1px solid" borderColor="gray.100" bg="white">
          <Avatar size="sm" name={contato?.nome || formatPhone(contato?.telefone)} bg="gray.300" />
          <Box flex="1" minW={0}>
            <Text fontWeight="bold" fontSize="sm" isTruncated>
              {contato?.nome || (contato ? formatPhone(contato.telefone) : "Carregando...")}
            </Text>
            <Text fontSize="xs" color="gray.500">{contato ? formatPhone(contato.telefone) : ""}</Text>
          </Box>
          <Tooltip label="Ligar pelo discador">
            <IconButton aria-label="Ligar" icon={<Phone size={18} />} size="sm" colorScheme="green" variant="ghost" onClick={ligar} />
          </Tooltip>
          <IconButton aria-label="Fechar" icon={<X size={18} />} size="sm" variant="ghost" onClick={onClose} />
        </Flex>

        <Tabs colorScheme="orange" index={tabIdx} onChange={setTabIdx} display="flex" flexDirection="column" flex="1" overflow="hidden" isLazy>
          <TabList px={3} flexShrink={0}>
            <Tab fontSize="sm" fontWeight="semibold">Conversa</Tab>
            <Tab fontSize="sm" fontWeight="semibold">Poli</Tab>
            <Tab fontSize="sm" fontWeight="semibold">Ficha</Tab>
          </TabList>
          <TabPanels flex="1" overflow="hidden">
            {/* ── CONVERSA ── */}
            <TabPanel p={0} h="100%" display="flex" flexDirection="column">
              <Flex px={3} py={2} borderBottom="1px solid" borderColor="gray.50" align="center" gap={2} flexShrink={0}>
                {buscaAberta ? (
                  <Input size="sm" autoFocus placeholder="Pesquisar mensagens..." value={busca}
                    onChange={(e) => setBusca(e.target.value)} />
                ) : <Box flex="1" />}
                <IconButton aria-label="Pesquisar" size="sm" variant="ghost"
                  icon={<Search size={16} />} onClick={() => { setBuscaAberta((v) => !v); setBusca(""); }} />
              </Flex>

              <Box ref={scrollRef} flex="1" overflowY="auto" bg="#efeae2" py={3} px={2}
                sx={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { bg: "rgba(0,0,0,0.15)", borderRadius: "10px" } }}>
                {loading ? (
                  <Center py={10}><Spinner color={ACCENT} /></Center>
                ) : msgs.length === 0 ? (
                  <Center py={10} color="gray.500"><Text fontSize="sm">{busca ? "Nada encontrado." : "Sem mensagens. Diga olá 👋"}</Text></Center>
                ) : (
                  <VStack spacing={1.5} align="stretch">
                    {!busca && msgs.length >= limite && (
                      <Center><Button size="xs" variant="ghost" onClick={() => setLimite((l) => l + 50)}>Ver anteriores</Button></Center>
                    )}
                    {msgs.map((m) => <Bubble key={m.id} m={m} />)}
                  </VStack>
                )}
              </Box>

              {/* Composer */}
              <Box borderTop="1px solid" borderColor="gray.100" p={2} bg="white" flexShrink={0}>
                {gravando ? (
                  <HStack>
                    <Box w={2.5} h={2.5} borderRadius="full" bg="red.500" sx={{ animation: "pulse 1s infinite" }} />
                    <Text fontSize="sm" color="red.500" flex="1">Gravando... {String(Math.floor(recSeg / 60)).padStart(2, "0")}:{String(recSeg % 60).padStart(2, "0")}</Text>
                    <Button size="sm" variant="ghost" onClick={cancelarGravacao}>Cancelar</Button>
                    <IconButton aria-label="Enviar áudio" colorScheme="green" icon={<Send size={16} />} size="sm" onClick={pararEnviarAudio} isLoading={enviando} />
                  </HStack>
                ) : pending ? (
                  <VStack align="stretch" spacing={2}>
                    <HStack bg="gray.50" p={2} borderRadius="md">
                      {pending.previewUrl
                        ? <Image src={pending.previewUrl} maxH="56px" borderRadius="md" />
                        : <FileText size={20} />}
                      <Text fontSize="xs" flex="1" noOfLines={1}>{pending.file.name}</Text>
                      <IconButton aria-label="Remover" size="xs" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setPending(null)} />
                    </HStack>
                    <HStack>
                      <Input size="sm" placeholder="Legenda (opcional)" value={caption} onChange={(e) => setCaption(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && enviarMidia()} />
                      <IconButton aria-label="Enviar" colorScheme="orange" icon={<Send size={16} />} size="sm" onClick={enviarMidia} isLoading={enviando} />
                    </HStack>
                  </VStack>
                ) : (
                  <HStack align="end">
                    <Menu placement="top-start">
                      <MenuButton as={IconButton} aria-label="Anexar" icon={<Paperclip size={18} />} size="sm" variant="ghost" />
                      <MenuList>
                        <MenuItem icon={<ImageIcon size={16} />} onClick={() => escolherArquivo("image")}>Imagem</MenuItem>
                        <MenuItem icon={<FileText size={16} />} onClick={() => escolherArquivo("document")}>Documento</MenuItem>
                      </MenuList>
                    </Menu>
                    <Textarea
                      value={texto} onChange={(e) => setTexto(e.target.value)}
                      placeholder="Digite uma mensagem..." rows={1} resize="none" size="sm" minH="38px" maxH="120px"
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarTexto(); } }}
                    />
                    {texto.trim() ? (
                      <IconButton aria-label="Enviar" colorScheme="orange" bg={ACCENT} color="white" _hover={{ bg: "#d4702a" }}
                        icon={<Send size={16} />} size="sm" onClick={enviarTexto} isLoading={enviando} />
                    ) : (
                      <IconButton aria-label="Gravar áudio" icon={<Mic size={18} />} size="sm" variant="ghost" onClick={iniciarGravacao} />
                    )}
                  </HStack>
                )}
                <input ref={fileRef} type="file" hidden onChange={onFileSelecionado} />
              </Box>
            </TabPanel>

            {/* ── POLI (histórico read-only do atendimento Poli) ── */}
            <TabPanel p={0} h="100%">
              <PoliTab contatoId={contatoId} />
            </TabPanel>

            {/* ── FICHA (componente unificado, mesmo da carteira/base) ── */}
            <TabPanel overflowY="auto" h="100%">
              {!contato ? <Center py={10}><Spinner color={ACCENT} /></Center> : (
                <FichaTab contatoId={contatoId} />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ModalContent>

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
    </Modal>
  );
}

// ── Ficha do contato: usa o componente unificado (idêntico ao da carteira/base) ──
function FichaTab({ contatoId }) {
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    if (!contatoId) return;
    setLoading(true);
    const r = await getCrmFichaPorContato(contatoId);
    setFicha(r || null);
    setLoading(false);
  }, [contatoId]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading || !ficha) return <Center py={10}><Spinner color={ACCENT} /></Center>;
  return (
    <Box py={2}>
      <FichaCliente ficha={ficha} onReload={carregar} emChat />
    </Box>
  );
}

// ── Aba Poli: histórico do atendimento Poli, somente leitura (reusa Bubble) ──
function PoliTab({ contatoId }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvido, setResolvido] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setLoading(true);
      const r = await getCrmMensagensPoli(contatoId);
      if (!vivo) return;
      setMsgs(r?.data ? [...r.data].reverse() : []); // backend devolve desc; exibimos asc
      setResolvido(r?.resolvido !== false);
      setLoading(false);
    })();
    return () => { vivo = false; };
  }, [contatoId]);

  useEffect(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs]);

  if (loading) return <Center py={10}><Spinner color={ACCENT} /></Center>;
  return (
    <Box ref={scrollRef} h="100%" overflowY="auto" bg="#efeae2" py={3} px={2}
      sx={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { bg: "rgba(0,0,0,0.15)", borderRadius: "10px" } }}>
      {msgs.length === 0 ? (
        <Center py={10} color="gray.500">
          <Text fontSize="sm">{resolvido ? "Sem conversas na Poli." : "Contato não localizado na Poli."}</Text>
        </Center>
      ) : (
        <VStack spacing={1.5} align="stretch">
          {msgs.map((m) => <Bubble key={m.id} m={m} comData />)}
        </VStack>
      )}
    </Box>
  );
}
